import { ReactNode, UIEvent, useCallback } from 'react';
import InboxOutlined from 'assets/svg/InboxOutlined';
import Loader from 'components/molecules/Loader';

export type GenericGridRow<TCell> = {
    bucketLabel: string;
    cells: Record<string, TCell | null | undefined>;
};

type GenericWhitespaceGridProps<TCell> = {
    columnHeaders: string[];
    gridRows: GenericGridRow<TCell>[];
    renderCell: (cell: NonNullable<TCell>, rowLabel: string, columnHeader: string) => ReactNode;
    emptyCellNode?: ReactNode;
    isFetched?: boolean;
    onLoadMoreRows?: () => void | Promise<void>;
    onLoadMoreColumns?: () => void | Promise<void>;
    hasMoreRows?: boolean;
    hasMoreColumns?: boolean;
    isLoadingMoreRows?: boolean;
    isLoadingMoreColumns?: boolean;
};

const DEFAULT_LOADING_NODE = (
    <div className="w-full h-[755px] bg-white rounded-lg">
        <Loader size="default" />
    </div>
);

const DEFAULT_EMPTY_CELL_NODE = (
    <div className="flex h-full min-h-[220px] items-center justify-center bg-[#f8f9fa] text-xs text-[#71717b]">
        No data
    </div>
);

const DEFAULT_EMPTY_NODE = (
    <div className="flex min-h-[758px] w-full items-center justify-center rounded-lg border border-[#e4e4e7] bg-[#f8f9fa] px-8">
        <div className="flex flex-col items-center justify-center gap-2 text-center">
            <InboxOutlined customIndex={0} />
            <div className="flex flex-col items-center gap-1">
                <p className="text-[16px] leading-8 font-semibold text-[rgba(0,0,0,0.88)]">No data found</p>
                <p className="text-[14px] leading-[22px] text-[rgba(0,0,0,0.45)]">No data found for the selected filters</p>
            </div>
        </div>
    </div>
);

const GenericWhitespaceGrid = <TCell,>({
    columnHeaders,
    gridRows,
    renderCell,
    emptyCellNode,
    isFetched = true,
    onLoadMoreRows,
    onLoadMoreColumns,
    hasMoreRows = false,
    hasMoreColumns = false,
    isLoadingMoreRows = false,
    isLoadingMoreColumns = false,
}: GenericWhitespaceGridProps<TCell>) => {
    const isPaginating = isLoadingMoreRows || isLoadingMoreColumns;

    const handleScroll = useCallback(
        (event: UIEvent<HTMLDivElement>) => {
            const target = event.currentTarget;
            const verticalRemaining = target.scrollHeight - target.scrollTop - target.clientHeight;
            const horizontalRemaining = target.scrollWidth - target.scrollLeft - target.clientWidth;
            const threshold = 80;

            if (verticalRemaining <= threshold && hasMoreRows && !isLoadingMoreRows) {
                onLoadMoreRows?.();
            }

            if (horizontalRemaining <= threshold && hasMoreColumns && !isLoadingMoreColumns) {
                onLoadMoreColumns?.();
            }
        },
        [hasMoreColumns, hasMoreRows, isLoadingMoreColumns, isLoadingMoreRows, onLoadMoreColumns, onLoadMoreRows],
    );

    if (!isFetched) {
        return <>{DEFAULT_LOADING_NODE}</>;
    }

    if (gridRows.length === 0) {
        return <>{DEFAULT_EMPTY_NODE}</>;
    }

    return (
        <div className="relative max-h-[758px] overflow-auto rounded-lg border border-[#e4e4e7]" onScroll={handleScroll}>
            {isPaginating ? (
                <div className="pointer-events-none absolute inset-0 z-40 flex items-center justify-center bg-white/30">
                    <Loader size="default" />
                </div>
            ) : null}
            <table className={`min-w-full w-max border-separate border-spacing-0 ${isPaginating ? 'opacity-50' : ''}`}>
                <thead>
                    <tr className="bg-[#fafafa]">
                        <th className="sticky left-0 top-0 z-30 whitespace-nowrap border border-[#e4e4e7] bg-[#fafafa] px-2 py-3 text-left text-[14px] font-semibold text-[#3f3f46]">
                            {''}
                        </th>
                        {columnHeaders.map((header) => (
                            <th
                                key={header}
                                className="sticky top-0 z-20 w-[225px] min-w-[225px] max-w-[225px] border border-[#e4e4e7] bg-[#fafafa] px-3 py-3 text-left text-[14px] font-semibold text-[#3f3f46]"
                            >
                                {header}
                            </th>
                        ))}
                        <th className="sticky top-0 z-20 w-full border-b border-[#e4e4e7] bg-[#fafafa]" aria-hidden />
                    </tr>
                </thead>
                <tbody>
                    {gridRows.map((row) => (
                        <tr key={row.bucketLabel} className="h-[220px]">
                            <td className="sticky whitespace-nowrap left-0 z-10 border border-[#e4e4e7] bg-[#fafafa] px-2 py-3 text-center align-middle text-xs font-bold text-[#3f3f46]">
                                {row.bucketLabel}
                            </td>
                            {columnHeaders.map((header) => {
                                const cell = row.cells[header];
                                return (
                                    <td
                                        key={`${row.bucketLabel}-${header}`}
                                        className="w-[225px] min-w-[225px] max-w-[225px] border border-[#e4e4e7] p-0 align-top h-[220px]"
                                    >
                                        {cell != null ? renderCell(cell as NonNullable<TCell>, row.bucketLabel, header) : (emptyCellNode ?? DEFAULT_EMPTY_CELL_NODE)}
                                    </td>
                                );
                            })}
                            <td className="w-full border-b border-[#e4e4e7] bg-white" aria-hidden />
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default GenericWhitespaceGrid;
